import { createAsyncThunk } from "@reduxjs/toolkit";
import { bookmarksService } from "../services/bookmarks.service";
import { handleRejectError } from "../../../utils/api/api.utils";
import { SavedProduct, PaginationMetadata } from "../interfaces/bookmarks.interface";
import {
  processSavedProductsResponse,
  processLikedProductsResponse
} from "../utils/bookmark.utils";

export type FetchSavedProductsParams = {
  page?: number;
  limit?: number;
  append?: boolean;
};

export const fetchSavedProducts = createAsyncThunk<
  {
    products: SavedProduct[];
    pagination: PaginationMetadata;
    append: boolean;
  },
  FetchSavedProductsParams
>(
  "bookmarks/fetchSavedProducts",
  async ({ page = 1, limit = 15, append = false }, { rejectWithValue }) => {
    try {
      const response = await bookmarksService.getSavedProducts(page, limit);

      const processedData = processSavedProductsResponse(response);

      return {
        products: processedData.products,
        pagination: processedData.pagination,
        append
      };
    } catch (error: any) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

export const toggleProductSaved = createAsyncThunk(
  "bookmarks/toggleProductSaved",
  async (
    {
      productId,
      action,
      product,
    }: {
      productId: string;
      action: "save" | "unsave";
      product?: any;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      if (action === "save") {
        await bookmarksService.saveProduct(productId);
      } else {
        await bookmarksService.unsaveProduct(productId);
      }
      // Keep savedProducts list consistent everywhere (Home, Bookmarks page, etc.)
      // by refreshing from the source of truth after a toggle.
      dispatch(fetchSavedProducts({}));
      return { productId, action, product };
    } catch (error: any) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

export type FetchLikedProductsParams = {
  page?: number;
  limit?: number;
  append?: boolean;
};

export const fetchLikedProducts = createAsyncThunk<
  {
    products: SavedProduct[];
    pagination: PaginationMetadata;
    append: boolean;
  },
  FetchLikedProductsParams
>(
  "bookmarks/fetchLikedProducts",
  async ({ page = 1, limit = 15, append = false }, { rejectWithValue }) => {
    try {
      const response = await bookmarksService.getLikedProducts(page, limit);

      const processedData = processLikedProductsResponse(response);

      return {
        products: processedData.products,
        pagination: processedData.pagination,
        append
      };
    } catch (error: any) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);

export const toggleProductLiked = createAsyncThunk(
  "bookmarks/toggleProductLiked",
  async (
    { productId, action }: { productId: string; action: "like" | "unlike" },
    { rejectWithValue },
  ) => {
    try {
      if (action === "like") {
        await bookmarksService.likeProduct(productId);
      } else {
        await bookmarksService.unlikeProduct(productId);
      }
      return { productId, action };
    } catch (error: any) {
      return handleRejectError(rejectWithValue, error);
    }
  },
);
